import React, { useEffect, useState } from 'react';
import { apiFetch } from './appFetch';
import { resolveUrl } from './api';
import { FONT_SERIF, FONT_UI } from './theme';

// ET6: det man ser när QR-koden på en köttetikett skannas (qvenari.com/?labelId=…).
// Alltid ljust kort, som etiketten. ET7: spårbarhet från det registrerade djuret.

export interface TraceStep { date?: string; by?: string }
export interface LabelTrace {
  gameType?: string; sex?: string; ageClass?: string;
  shot?: TraceStep & { lat?: number; lng?: number };
  hung?: TraceStep; cut?: TraceStep;
  photos?: string[];
}

export interface ScannedLabel {
  id: string; species?: string; cut?: string; markName?: string;
  skjutetDate?: string; styckatDate?: string; weightKg?: string;
  kgPrice?: string; totalPrice?: string; notes?: string; labelType?: string;
  trace?: LabelTrace | null;
}

type State = { status: 'loading' } | { status: 'notFound' } | { status: 'error' } | { status: 'ok'; label: ScannedLabel };

export async function fetchScannedLabel(id: string): Promise<State> {
  try {
    const res = await apiFetch(`/api/labels/${encodeURIComponent(id)}`);
    if (res.status === 404) return { status: 'notFound' };
    if (!res.ok) return { status: 'error' };
    return { status: 'ok', label: await res.json() as ScannedLabel };
  } catch {
    return { status: 'error' };
  }
}

const INK = '#1d261d';
const MUTED = '#6b7166';
const ACCENT = '#a4560a';

// ── Liten karta: OSM-rutor runt skottplatsen (ingen kartmotor behövs) ──────
const TILE = 256;
export function tileGrid(lat: number, lng: number, zoom: number, w: number, h: number) {
  const n = 2 ** zoom;
  const rad = lat * Math.PI / 180;
  const px = (lng + 180) / 360 * n * TILE;
  const py = (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * n * TILE;
  const left = px - w / 2; const top = py - h / 2;
  const tiles: { x: number; y: number; left: number; top: number }[] = [];
  for (let x = Math.floor(left / TILE); x * TILE < left + w; x++) {
    for (let y = Math.floor(top / TILE); y * TILE < top + h; y++) {
      tiles.push({ x: ((x % n) + n) % n, y, left: x * TILE - left, top: y * TILE - top });
    }
  }
  /** Meter per pixel på den breddgraden (för cirkeln som visar avrundningen). */
  const mPerPx = 156543.03392 * Math.cos(rad) / n;
  return { tiles, mPerPx };
}

function ShotMap({ lat, lng }: { lat: number; lng: number }) {
  const W = 340; const H = 170; const Z = 14;
  const { tiles, mPerPx } = tileGrid(lat, lng, Z, W, H);
  const r = Math.max(8, 100 / mPerPx);
  return (
    <div style={{ marginTop: 8 }}>
      <div data-testid="shot-map" style={{ position: 'relative', width: '100%', maxWidth: W, height: H, overflow: 'hidden', borderRadius: 8, background: '#e8e4da', margin: '0 auto' }}>
        <div style={{ position: 'absolute', left: '50%', top: 0, width: W, height: H, marginLeft: -W / 2 }}>
          {tiles.map(t => (
            <img key={`${t.x}-${t.y}`} src={`https://tile.openstreetmap.org/${Z}/${t.x}/${t.y}.png`} alt="" draggable={false}
              style={{ position: 'absolute', left: t.left, top: t.top, width: TILE, height: TILE }} />
          ))}
          <div style={{ position: 'absolute', left: W / 2 - r, top: H / 2 - r, width: r * 2, height: r * 2, borderRadius: '50%', background: 'rgba(164,86,10,0.22)', border: '2px solid #a4560a', boxSizing: 'border-box' }} />
          <div style={{ position: 'absolute', left: W / 2 - 4, top: H / 2 - 4, width: 8, height: 8, borderRadius: '50%', background: '#a4560a' }} />
        </div>
        <div style={{ position: 'absolute', right: 0, bottom: 0, background: 'rgba(255,255,255,0.85)', fontSize: 10, padding: '1px 4px', color: '#333' }}>© OpenStreetMap</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED, marginTop: 4 }}>
        <span>Ungefärlig plats (±100 m)</span>
        <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=14/${lat}/${lng}`} target="_blank" rel="noreferrer" style={{ color: ACCENT }}>Öppna karta</a>
      </div>
    </div>
  );
}

function TraceRow({ title, step, children }: { title: string; step?: TraceStep; children?: React.ReactNode }) {
  if (!step?.date && !step?.by && !children) return null;
  return (
    <li style={{ position: 'relative', paddingLeft: 22, paddingBottom: 14 }}>
      <span style={{ position: 'absolute', left: 0, top: 4, width: 10, height: 10, borderRadius: '50%', background: ACCENT }} />
      <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
      <div style={{ fontSize: 14, color: INK }}>
        {[step?.date, step?.by && `av ${step.by}`].filter(Boolean).join(' · ')}
      </div>
      {children}
    </li>
  );
}

export function TraceSection({ trace, styckatDate }: { trace: LabelTrace; styckatDate?: string }) {
  const shot = trace.shot;
  const cut = trace.cut ?? (styckatDate ? { date: styckatDate } : undefined);
  const animal = [trace.gameType, trace.sex, trace.ageClass].filter(Boolean).join(', ');
  const photos = trace.photos ?? [];
  const hasSteps = !!(shot?.date || shot?.by || shot?.lat != null || trace.hung || cut);
  if (!hasSteps && !photos.length) return null;
  return (
    <section aria-label="Spårbarhet" style={{ borderTop: '1px solid rgba(0,0,0,0.1)', marginTop: 12, paddingTop: 12 }}>
      <div style={{ color: MUTED, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
        Spårbarhet{animal ? ` · ${animal}` : ''}
      </div>
      {hasSteps && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: '2px solid #e2dccd', marginLeft: 4 }}>
          <TraceRow title="Skjuten" step={shot}>
            {shot?.lat != null && shot?.lng != null && <ShotMap lat={shot.lat} lng={shot.lng} />}
          </TraceRow>
          <TraceRow title="Upphängd" step={trace.hung} />
          <TraceRow title="Styckad" step={cut} />
        </ol>
      )}
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 4 }}>
          {photos.map(u => (
            <a key={u} href={resolveUrl(u)} target="_blank" rel="noreferrer" style={{ display: 'block', aspectRatio: '1', borderRadius: 6, overflow: 'hidden', background: '#eee' }}>
              <img src={resolveUrl(u)} alt="Foto av djuret" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

export function LabelView({ id, onClose }: { id: string; onClose: () => void }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });
    fetchScannedLabel(id).then(s => { if (alive) setState(s); });
    return () => { alive = false; };
  }, [id]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Köttetikett"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 420, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: '22px 20px', color: INK, fontFamily: FONT_UI, position: 'relative', boxSizing: 'border-box' }}>
        <button type="button" onClick={onClose} aria-label="Stäng"
          style={{ position: 'absolute', top: 8, right: 8, width: 44, height: 44, background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: MUTED, fontFamily: 'inherit' }}>×</button>

        <div role="img" aria-label="Qvenari" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginBottom: 18 }}>
          <img src="/qvenari-logo.png" alt="" style={{ height: 28, width: 'auto' }} />
          <span style={{ fontFamily: FONT_SERIF, fontSize: 28, color: INK, lineHeight: 1, marginTop: 4, letterSpacing: '-0.5px' }}>venari</span>
        </div>

        {state.status === 'loading' && <div style={{ textAlign: 'center', padding: 20, color: MUTED }}>Hämtar etiketten…</div>}
        {state.status === 'notFound' && <div style={{ color: '#b3261e', textAlign: 'center', padding: 16, lineHeight: 1.5 }}>Etiketten hittades inte. Den har inte sparats i Qvenari.</div>}
        {state.status === 'error' && <div style={{ color: '#b3261e', textAlign: 'center', padding: 16, lineHeight: 1.5 }}>Etiketten kunde inte hämtas just nu. Försök igen om en stund.</div>}

        {state.status === 'ok' && (() => {
          const l = state.label;
          const rows: [string, string | undefined, boolean?][] = [
            ['Jaktmark', l.markName],
            ...(l.trace ? [] : [['Styckat', l.styckatDate], ['Skjutet', l.skjutetDate]] as [string, string | undefined][]),
            ['Vikt', l.weightKg && `${l.weightKg} kg`],
            ['Kilopris', l.kgPrice && `${l.kgPrice} kr/kg`],
            ['Pris', l.totalPrice && `${l.totalPrice} kr`, true],
          ];
          return (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l.species || 'Vilt'}</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 16 }}>{l.cut || '—'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', fontSize: 15, marginBottom: 12 }}>
                {rows.filter(([, v]) => v).map(([k, v, strong]) => (
                  <div key={k}>
                    <span style={{ color: MUTED, fontSize: 11, fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{k}</span>
                    <span style={{ fontWeight: strong ? 800 : 600 }}>{v}</span>
                  </div>
                ))}
              </div>
              {l.trace && <TraceSection trace={l.trace} styckatDate={l.styckatDate} />}
              {l.notes && <div style={{ fontSize: 13, color: MUTED, fontStyle: 'italic', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 10 }}>{l.notes}</div>}
              <div style={{ marginTop: 12, fontSize: 11, color: MUTED, textAlign: 'right' }}>Etikett {id.slice(0, 8).toUpperCase()}</div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
